import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysInvoice } from '../queryKeys';

import {
  type AdditionalNoteRequest,
  type AdditionalNoteResponse,
} from './types';
import {
  zodAdditionalNoteRequestSchema,
  zodAdditionalNoteResponseSchema,
} from './validations';

type Variables = { invoiceId: UUID } & AdditionalNoteRequest;

/**
 * Creates or updates an additional note for an invoice.
 *
 * @param invoiceId - The ID of the invoice.
 * @param note - The note text to create or update.
 * @returns The created or updated additional note.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/create_or_update_invoice_additional_note_invoice__invoice_id__additional_note_post
 */
export const upsertInvoiceAdditionalNote = async (
  invoiceId: UUID,
  note: string,
): Promise<AdditionalNoteResponse> => {
  const response = await vestigasApi
    .post(ENDPOINT.INVOICE.UPSERT_ADDITIONAL_NOTE(invoiceId), {
      json: { note },
      validate: {
        body: zodAdditionalNoteRequestSchema,
      },
    })
    .json<AdditionalNoteResponse>();

  return zodAdditionalNoteResponseSchema.parse(response);
};

/**
 * React Query mutation hook for creating or updating an additional note on an invoice.
 *
 * @param options - Optional TanStack Query mutation options.
 * @returns A mutation object to trigger the upsert.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/create_or_update_invoice_additional_note_invoice__invoice_id__additional_note_post
 */
export const useMutationUpsertInvoiceAdditionalNote = (
  options?: Omit<
    UseMutationOptions<AdditionalNoteResponse, Error, Variables>,
    'mutationFn' | 'onSettled'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoiceId, note }: Variables) =>
      upsertInvoiceAdditionalNote(invoiceId, note),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoice.get(variables.invoiceId),
      });
    },
    ...options,
  });
};
